import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { BookOpenText, PaperPlaneTilt } from "@phosphor-icons/react";

export default function PlaybookManager() {
  const { activeBiz } = useOutletContext();
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);

  const sendPlaybook = async (e) => {
    e.preventDefault();
    const normalized = email.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(normalized)) {
      toast.error("Enter a valid customer email address");
      return;
    }

    setSending(true);
    try {
      const { data } = await api.post("/playbook/send", {
        email: normalized,
        business_name: activeBiz?.name || "",
      });
      setSent(data);
      toast.success(`Playbook sent to ${normalized}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Playbook email could not be sent");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-5xl">
      <header className="mb-6">
        <div className="label-tiny mb-1">Customer Resources</div>
        <h1 className="font-display text-4xl font-black tracking-tighter" data-testid="playbook-manager-title">
          Opportunity Playbook
        </h1>
        <p className="text-zinc-500 mt-2 max-w-2xl">
          Send the Service Business Opportunity Playbook directly to a customer.
        </p>
      </header>

      <section className="bento p-6 lg:p-8" data-testid="playbook-manager-card">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 flex items-center justify-center bg-blue-50 text-[#002FA7] rounded-full">
            <BookOpenText size={22} weight="duotone" />
          </div>
          <div>
            <h2 className="font-display text-xl font-bold">The Service Business Opportunity Playbook</h2>
            <p className="text-xs text-zinc-500 mt-0.5">By David Gilliam · RankTrust</p>
          </div>
        </div>

        <form onSubmit={sendPlaybook} className="max-w-2xl" data-testid="playbook-manager-form">
          <label className="label-tiny mb-1.5 block" htmlFor="playbook-manager-email">Customer email</label>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              id="playbook-manager-email"
              type="email"
              autoComplete="email"
              className="input-field flex-1 min-w-0"
              placeholder="customer@business.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="playbook-manager-email"
            />
            <button
              type="submit"
              className="btn-primary justify-center whitespace-nowrap"
              disabled={sending}
              data-testid="playbook-manager-send"
            >
              {sending ? (
                <><span className="spinner"></span> Sending</>
              ) : (
                <><PaperPlaneTilt size={14} weight="bold" /> Send Playbook</>
              )}
            </button>
          </div>
        </form>

        {activeBiz?.name && (
          <p className="text-xs text-zinc-500 mt-3">Active business: <span className="font-semibold text-zinc-700">{activeBiz.name}</span></p>
        )}

        {sent?.ok && (
          <div className="mt-5 p-4 border border-emerald-200 bg-emerald-50 text-emerald-800 text-sm" data-testid="playbook-manager-success">
            Playbook sent successfully to <strong>{sent.email}</strong>.
          </div>
        )}
      </section>
    </div>
  );
}
